import { useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import { Popover, Transition } from '@headlessui/react';
import { CodeBracketIcon } from '@heroicons/react/24/outline';

import { isProd } from '../../config';
import { ChatBubble } from './ChatBubble';
import { Avatar } from '../shared/Avatar';
import { ChatSuggestions } from './ChatSuggestions';
import OrbitSprite from '../../assets/orbit-sprite.png';
import { useUserContext } from '../../contexts/UserContext';
import { ChatMessage, ChatRole } from '../../types/Orbit/Chat';
import useOrbitChatMutation from '../../data/orbit/useOrbitChatMutation';
import useOrbitChatMessages from '../../data/orbit/useOrbitChatMessages';
import useOrganisationUsers from '../../data/organisationUsers/useOrganisationUsers';
import { Button } from '../base/Button';

interface ChatProps {
  className?: string;
  chatId: string;
  chatTitle?: string;
  isExistingChat?: boolean;
}

export function Chat({
  className,
  chatId,
  chatTitle,
  isExistingChat,
}: ChatProps) {
  const { userId } = useUserContext();
  const { users } = useOrganisationUsers();
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [thinking, setThinking] = useState(false);

  const { sendMessage } = useOrbitChatMutation();
  const { messages: loadedMessages } = useOrbitChatMessages(
    isExistingChat ? chatId : undefined
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const participants = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const m of messages) {
      if (m.userId) {
        const id = String(m.userId);
        if (!seen.has(id)) {
          seen.add(id);
          list.push(id);
        }
      }
    }
    return list;
  }, [messages]);

  const currentUser = useMemo(() => {
    return users.data?.find((u) => u.id === userId);
  }, [users.data, userId]);

  useEffect(() => {
    const loadedChat = loadedMessages.data?.[0];
    const loadedChatMessages = Array.isArray(loadedChat?.messages)
      ? loadedChat.messages
      : [];

    if (!loadedChat) {
      setMessages([]);
      return;
    }

    setMessages(
      loadedChatMessages.map((m) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }))
    );
  }, [loadedMessages.data]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current !== null) {
        clearTimeout(focusTimeoutRef.current);
      }

      if (thinkingTimeoutRef.current !== null) {
        clearTimeout(thinkingTimeoutRef.current);
      }
    };
  }, []);

  const _sendMessage = async (message: string) => {
    setProcessing(true);
    thinkingTimeoutRef.current = setTimeout(() => {
      setThinking(true);
    }, 1000);

    const userMsg: ChatMessage = {
      role: ChatRole.User,
      userId: userId,
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    try {
      const response = await sendMessage.mutateAsync({ chatId, message });

      const assistantMsg: ChatMessage = {
        role: ChatRole.Assistant,
        content: response[0].reply,
        timestamp: new Date(),
      };

      if (response[0].title) {
        setTitle(response[0].title);
      }

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (e) {
      console.error('Failed to send chat message', e);

      let errorMsg: ChatMessage = {
        role: ChatRole.Assistant,
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
      };

      if (thinkingTimeoutRef.current !== null) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }

      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setProcessing(false);
      setThinking(false);

      focusTimeoutRef.current = setTimeout(() => {
        inputRef.current?.focus();
        focusTimeoutRef.current = null;
      }, 150);
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (processing) return;

    await _sendMessage(suggestion);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || processing) return;

    await _sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      if (input.trim() && !processing) {
        handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
      }

      return;
    }

    if (e.key !== 'ArrowUp') return;
    if (input.trim().length > 0) return;
    e.preventDefault();

    const lastUser = [...messages].reverse().find((m) => m.role === ChatRole.User);
    const source = lastUser ?? (messages.length ? messages[messages.length - 1] : null);
    if (!source) return;

    setInput(source.content);
    focusTimeoutRef.current = setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = source.content.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          // Ignore if it fails (e.g. input is not focused)
        }
      }
      focusTimeoutRef.current = null;
    }, 50);
  };

  return (
    <div className={`flex flex-col h-full ${className ?? ''}`}>
      <div className="pl-6 pr-4 transition-all flex items-center justify-between duration-300 ease-in-out data-[closed]:max-h-0 data-[closed]:opacity-0 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="py-5 flex items-center justify-between">
          <h2 className="text-lg font-medium dark:text-white">{title ?? chatTitle ?? 'New Chat'}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center -space-x-3">
            {!isExistingChat ? (
              <div className="inline-block ring-2 ring-white dark:ring-gray-800 rounded-full">
                <Avatar id={userId!} forename={currentUser?.forename ?? ''} surname={currentUser?.surname ?? ''} size={8} />
              </div>
            ) : (
              participants.map((id) => {
                const user = users.data?.find((u) => u.id === id);
                return (
                  <div key={id} className="inline-block ring-2 ring-white dark:ring-gray-800 rounded-full">
                    <Avatar id={id} forename={user?.forename ?? ''} surname={user?.surname ?? ''} size={8} />
                  </div>
                )
              })
            )}
          </div>
          {!isProd && (
            <Popover className="relative">
              <Popover.Button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                <CodeBracketIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </Popover.Button>

              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Popover.Panel className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-3 z-50">
                  <div className="text-sm text-gray-700 dark:text-gray-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Debug</span>
                      <span className="text-xs text-gray-400">{isExistingChat ? 'Existing' : 'New'}</span>
                    </div>
                    <div className="text-xs text-gray-400">Chat ID: <span className="font-mono text-gray-700 dark:text-gray-200 break-all">{chatId}</span></div>
                    <div className="text-xs text-gray-400">Title: <span className="text-gray-700 dark:text-gray-200">{title ?? chatTitle ?? '—'}</span></div>
                    <div className="text-xs text-gray-400">User ID: <span className="text-gray-700 dark:text-gray-200">{userId ?? '—'}</span></div>
                    <div className="text-xs text-gray-400">Messages: <span className="text-gray-700 dark:text-gray-200">{messages.length}</span></div>
                    <div className="text-xs text-gray-400 truncate">Last: <span className="text-gray-700 dark:text-gray-200">{messages.length ? messages[messages.length - 1].content : '—'}</span></div>

                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(JSON.stringify(messages, null, 2))}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded"
                      >
                        Copy JSON
                      </button>
                      <button
                        type="button"
                        onClick={() => console.log('Debug messages:', messages)}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded"
                      >
                        Log
                      </button>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </Popover>
          )}
        </div>
      </div>


      <div
        className={clsx(
          'flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 p-6 bg-gray-400/5 dark:bg-white/5',
          messages.length === 0 && 'justify-around',
        )}
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center text-black dark:text-white animate-[fade-in-up_0.4s_ease-out_both]">
            <img src={OrbitSprite} alt="Orbit" className="w-32 h-32 mb-6" />
            <h1 className="text-4xl font-medium font-sans">Hello, I'm Orbit.</h1>
            <h3 className="text-lg mt-4 text-center">
              Here to help with your questions and optimisation of your renewable Appliances.
            </h3>
            <p className="text-center text-gray-400 mt-2 text-sm">
              Pick a prompt from below or just type your query
            </p>
          </div>
        )}
        {messages.length === 0 && <ChatSuggestions onSuggestionClick={handleSuggestionClick} />}
        {messages.map((msg, index) => (
          <ChatBubble key={index} message={msg} />
        ))}
        {thinking && (
          <div className="self-start flex items-center gap-1.5 px-4 py-3 rounded-xl rounded-bl-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 animate-[bubble-from-left_0.2s_ease-out_both]">
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-[typing-dot_1.2s_ease-in-out_infinite]"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-[typing-dot_1.2s_ease-in-out_infinite]"
              style={{ animationDelay: '200ms' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-[typing-dot_1.2s_ease-in-out_infinite]"
              style={{ animationDelay: '400ms' }}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-2 pl-5 pr-3 py-3 border-t border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div className="flex gap-2">
          <div className="flex flex-1 items-center min-h-10">
            <textarea
              rows={1}
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onInput={() => {
                const el = inputRef.current;
                if (!el) return;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
              }}
              onKeyDown={handleKeyDown}
              placeholder={'Type a message...'}
              disabled={processing}
              autoFocus
              className="w-full text-black dark:text-white border-0 focus:ring-0 focus:border-0 focus:outline-0 resize-none overflow-hidden bg-transparent leading-normal py-0"
            />
          </div>
          <Button
            className="shrink-0 self-end px-4 py-2 bg-pd-blue text-white rounded-md disabled:opacity-50"
            type="submit"
            disabled={processing || !input.trim()}
          >
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
